import { useEffect, useState } from 'react'
import { useI18n } from '../i18n/useI18n'

// Mirrors DELIVERY_APPROVAL_WINDOW_MINUTES in services/pay/api/routers/orders.py.
// Change both together — the backend lazy-expire is authoritative, the UI
// just counts down to match.
const APPROVAL_WINDOW_MS = 10 * 60 * 1000

interface Props {
  createdAtIso: string
  /** Owner vs customer wording. Defaults to customer. */
  audience?: 'owner' | 'customer'
}

/**
 * #284 live countdown for the delivery approval window. Shown under the
 * "受付中" state on both sides — owners see "あと 7:23 で自動却下 (手数料
 * 0円)"、customers see "店舗の確認待ち (あと 7:23)".
 */
export default function DeliveryApprovalCountdown({ createdAtIso, audience = 'customer' }: Props) {
  const { t } = useI18n()
  const [remainingMs, setRemainingMs] = useState(() => {
    const createdAt = new Date(createdAtIso).getTime()
    return Math.max(0, createdAt + APPROVAL_WINDOW_MS - Date.now())
  })

  useEffect(() => {
    const tick = () => {
      const createdAt = new Date(createdAtIso).getTime()
      setRemainingMs(Math.max(0, createdAt + APPROVAL_WINDOW_MS - Date.now()))
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [createdAtIso])

  if (remainingMs <= 0) {
    return (
      <span className="font-body text-[11px] text-red-600">
        {t('delivery.approvalExpired')}
      </span>
    )
  }

  const mm = Math.floor(remainingMs / 60000)
  const ss = Math.floor((remainingMs % 60000) / 1000)
  const label = `${mm}:${ss.toString().padStart(2, '0')}`

  return (
    <span className={`font-body text-[11px] font-semibold ${
      remainingMs < 60000 ? 'text-red-600' : 'text-amber-700'
    }`}>
      {audience === 'owner'
        ? t('delivery.approvalCountdownOwner', { time: label })
        : t('delivery.approvalCountdownCustomer', { time: label })}
    </span>
  )
}
